/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Table } from 'carbon-addons-iot-react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';

import { messages } from './CommonTable';

const propTypes = {
  i18n: PropTypes.objectOf(PropTypes.string),
  /** Optional table actions */
  actions: PropTypes.objectOf(PropTypes.object),
};

const defaultProps = {
  i18n: null,
  actions: null,
};

/**
 * Wraps around the basic carbon addons Table, relinquishing control of everything to the parent component (unlike CommonTable which wraps around a stateful table)
 * Basically just adds i8n stuff
 * Used for those tables that tie in with Formik state, e.g. MetadataEditor, SchemaEditor
 */
const SimpleCommonTable = props => {
  const { formatMessage, locale } = useIntl();
  const { i18n } = props;
  return (
    <Table
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      locale={locale}
      i18n={merge(
        {},
        {
          /** pagination */
          pageBackwardAria: formatMessage(messages.previousPage),
          pageForwardAria: formatMessage(messages.nextPage),
          pageNumberAria: formatMessage(messages.pageNumber),
          itemsPerPage: formatMessage(messages.itemsPerPage),
          itemsRange: (min, max) => formatMessage(messages.itemsRange, { min, max }),
          currentPage: page => formatMessage(messages.currentPage, { page }),
          itemsRangeWithTotal: (min, max, total) =>
            formatMessage(messages.itemsRangeWithTotal, { min, max, total }),
          pageRange: (current, total) => formatMessage(messages.pageRange, { current, total }),
          /** table body */
          overflowMenuAria: formatMessage(messages.moreActions),
          clickToExpandAria: formatMessage(messages.clickToExpand),
          clickToCollapseAria: formatMessage(messages.clickToCollapse),
          selectAllAria: formatMessage(messages.selectAll),
          selectRowAria: formatMessage(messages.selectRow),
          /** toolbar */
          searchPlaceholder: formatMessage(messages.search),
          clearAllFilters: formatMessage(messages.clearAllFilters),
          columnSelectionButtonAria: formatMessage(messages.columnSelection),
          filterButtonAria: formatMessage(messages.filters),
          clearFilterAria: formatMessage(messages.clearFilter),
          filterAria: formatMessage(messages.filter),
          openMenuAria: formatMessage(messages.openMenu),
          closeMenuAria: formatMessage(messages.closeMenu),
          clearSelectionAria: formatMessage(messages.clearSelection),
          /** empty state */
          emptyMessage: formatMessage(messages.emptyMessage),
          emptyMessageWithFilters: formatMessage(messages.emptyMessageWithFilters),
          // eslint-disable-next-line react/destructuring-assignment
          emptyButtonLabel: props?.actions?.table?.onEmptyStateAction
            ? formatMessage(messages.emptyButtonLabel)
            : undefined, // leave off button if no action is defined
          /** Row actions test */
          inProgressText: formatMessage(messages.inProgress),
          actionFailedText: formatMessage(messages.actionFailed),
          learnMoreText: formatMessage(messages.learnMore),
          dismissText: formatMessage(messages.dismiss),
          /** selected actions */
          batchCancel: formatMessage(messages.cancel),
          itemSelected: formatMessage(messages.itemSelected),
          itemsSelected: formatMessage(messages.itemsSelected),
          filterNone: formatMessage(messages.filterNone),
          filterAscending: formatMessage(messages.filterAscending),
          filterDescending: formatMessage(messages.filterDescending),
          editButtonAria: formatMessage(messages.editButtonAria),
        },
        i18n // use whatever props come in
      )}
    />
  );
};

SimpleCommonTable.propTypes = propTypes;
SimpleCommonTable.defaultProps = defaultProps;

export default SimpleCommonTable;
